"use client";

import { AlertCircle, CheckCircle2 } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { useFormStatus } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ActionState } from "@/lib/action-state";
import { cn } from "@/lib/utils";

export function FormAlert({ state }: { state: ActionState }) {
  if (state.error) {
    return (
      <div
        role="alert"
        className="flex gap-2.5 rounded-xl bg-[var(--status-rejected-bg)] p-3.5 text-sm text-[var(--status-rejected-fg)]"
      >
        <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>{state.error}</span>
      </div>
    );
  }
  if (state.message) {
    return (
      <div
        role="status"
        className="flex gap-2.5 rounded-xl bg-[var(--status-approved-bg)] p-3.5 text-sm text-[var(--status-approved-fg)]"
      >
        <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>{state.message}</span>
      </div>
    );
  }
  return null;
}

type FieldProps = ComponentProps<typeof Input> & {
  name: string;
  label: string;
  state?: ActionState;
  hint?: ReactNode;
};

/** Input berlabel dengan pesan error dari server action. */
export function Field({ name, label, state, hint, className, defaultValue, ...props }: FieldProps) {
  const error = state?.fieldErrors?.[name];
  const id = props.id ?? `f-${name}`;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        name={name}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        defaultValue={state?.values?.[name] ?? defaultValue}
        {...props}
      />
      {error ? (
        <span id={`${id}-error`} className="text-[13px] font-semibold text-destructive">
          {error}
        </span>
      ) : hint ? (
        <span id={`${id}-hint`} className="text-[13px] text-muted-foreground">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function SubmitButton({
  children,
  pendingText,
  ...props
}: ComponentProps<typeof Button> & { pendingText?: string }) {
  const { pending } = useFormStatus();
  return (
    <Button type="submit" disabled={pending || props.disabled} aria-busy={pending} {...props}>
      {pending ? (pendingText ?? "Memproses…") : children}
    </Button>
  );
}
